'use client';

import { useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';

/**
 * YouTube-style top loading bar in site theme colors.
 * Uses real navigation events + a simulated trickle for perceived speed.
 */
export default function TopLoader() {
  const barRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef(0);

  const setWidth = useCallback((pct: number) => {
    progressRef.current = pct;
    if (barRef.current) {
      barRef.current.style.width = `${pct}%`;
      barRef.current.style.opacity = pct > 0 && pct < 100 ? '1' : '0';
    }
  }, []);

  const start = useCallback(() => {
    setWidth(0);
    // Small delay so opacity transition from 0 works
    requestAnimationFrame(() => {
      setWidth(15);
      timerRef.current = setInterval(() => {
        const p = progressRef.current;
        if (p < 30) setWidth(p + 3);
        else if (p < 60) setWidth(p + 1.5);
        else if (p < 85) setWidth(p + 0.4);
        // Stop at 85 — waits for finish()
      }, 100);
    });
  }, [setWidth]);

  const finish = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setWidth(100);
    setTimeout(() => setWidth(0), 350);
  }, [setWidth]);

  // Trigger on pathname change
  useEffect(() => {
    start();
    const t = setTimeout(finish, 700); // Increased delay to make the thicc loader shine
    return () => { clearTimeout(t); if (timerRef.current) clearInterval(timerRef.current); };
  }, [pathname, start, finish]);

  return (
    <div 
      className="top-loader-track" 
      role="progressbar" 
      aria-label="Loading progress" 
      aria-valuemin={0} 
      aria-valuemax={100} 
      aria-valuenow={progressRef.current}
    >
      <div ref={barRef} className="top-loader-bar" />
    </div>
  );
}
